/**
 * Dates in equility-web (EQ-2353).
 *
 * Display: every full calendar date a person reads is `MM/DD/YYYY`.
 *   formatDate(x)                    // "01/01/2026" (DATE_FORMAT)
 *   formatDateTime(x)                // "01/01/2026 9:30 AM" (DATE_TIME_FORMAT)
 *   formatInstantDate(at, companyTz) // an instant as a day, "09/25/2026"
 *   date-fns: format(d, DATEFNS_DATE_FORMAT)
 *
 * Wire: values sent to the API, query params, sort keys and filenames keep
 * their own format. Pass it explicitly, never rely on the display default:
 *   formatDate(x, WIRE_DATE_FORMAT)          // "2026-01-01"
 *   formatDate(x, LEGACY_LONG_DATE_FORMAT)   // "January 1, 2026", wire only
 *
 * Input: use `DatePicker` / `DatePicker.RangePicker` from `components/dates`,
 * never antd's day picker directly. The wrapper accepts `010126`, `1/1/26`,
 * `01012026`, `1/1/2026` and ISO `2026-01-01`, and its `onChange` second
 * argument is ISO.
 *
 * `parseDate(x)` reads a stored value (ISO, Date, Dayjs). `parseTypedDate(text)`
 * reads what a person typed, strictly, against the input lists below, and
 * returns `null` when it cannot.
 */
import dayjs, { type Dayjs } from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import customParseFormat from "dayjs/plugin/customParseFormat";
import isBetween from "dayjs/plugin/isBetween";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import localeData from "dayjs/plugin/localeData";
import localizedFormat from "dayjs/plugin/localizedFormat";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import relativeTime from "dayjs/plugin/relativeTime";
import weekday from "dayjs/plugin/weekday";

dayjs.locale("en");
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(localizedFormat);
dayjs.extend(isBetween);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(relativeTime);
dayjs.extend(quarterOfYear);
dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);

/** Display format for a calendar date. */
export const DATE_FORMAT = "MM/DD/YYYY";
/** Display format for a date with a time. */
export const DATE_TIME_FORMAT = "MM/DD/YYYY h:mm A";
/** `DATE_FORMAT` spelled for date-fns `format()`. */
export const DATEFNS_DATE_FORMAT = "MM/dd/yyyy";
/** Financial Review period column headers (EQ-2416): `Sep 2026`, `Oct`, `2026`. */
export const PERIOD_MONTH_FORMAT = "MMM YYYY";
export const PERIOD_MONTH_SHORT_FORMAT = "MMM";
export const PERIOD_YEAR_FORMAT = "YYYY";
/** Wire only: API bodies, query params, sort and group keys. */
export const WIRE_DATE_FORMAT = "YYYY-MM-DD";
/**
 * Wire only: the old `formatDate` default ("January 1, 2026"). For a site that
 * must keep sending or comparing that exact text. Never for display.
 */
export const LEGACY_LONG_DATE_FORMAT = "LL";

/**
 * The formats given to rc-picker. rc-picker strict-parses the typed text
 * against each entry on every keystroke and commits the first match, so every
 * entry has a four-digit year: no partly typed date can match early. The
 * first entry is what the field displays; `YYYY-MM-DD` keeps ISO paste working.
 */
export const DATE_INPUT_FORMATS = [
	DATE_FORMAT,
	"M/D/YYYY",
	"MM/D/YYYY",
	"M/DD/YYYY",
	"MMDDYYYY",
	WIRE_DATE_FORMAT,
];

/**
 * Two-digit-year forms. Never given to rc-picker (typing `01/01/2026` would
 * commit 2020 at `01/01/20`). The DatePicker wrapper rewrites text in these
 * forms to `MM/DD/YYYY` with `normalizeShortYearText` when the field is left.
 */
export const SHORT_YEAR_INPUT_FORMATS = [
	"MMDDYY",
	"M/D/YY",
	"MM/DD/YY",
	"MM/D/YY",
	"M/DD/YY",
];

// `hh` reads a zero-padded hour (`09:30 AM`), which strict `h` rejects.
const TIME_SUFFIXES = [" h:mm A", " h:mm a", " hh:mm A", " hh:mm a"];
const withTimeSuffixes = (formats: string[]) =>
	formats.flatMap((f) => TIME_SUFFIXES.map((t) => f + t));

/** `DATE_INPUT_FORMATS` with a time; the first entry is `DATE_TIME_FORMAT`. */
export const DATE_TIME_INPUT_FORMATS = withTimeSuffixes(DATE_INPUT_FORMATS);
/** `SHORT_YEAR_INPUT_FORMATS` with a time. Never given to rc-picker. */
export const SHORT_YEAR_DATE_TIME_INPUT_FORMATS = withTimeSuffixes(
	SHORT_YEAR_INPUT_FORMATS,
);

interface TypedDateOptions {
	/** Text carries a time (`h:mm AM`), as in a `showTime` picker. */
	withTime?: boolean;
	/** Reference day for the two-digit-year window. Defaults to today. */
	now?: Dayjs;
}

const SHORT_DATE_PATTERNS = [
	/^(\d{2})(\d{2})(\d{2})$/, // MMDDYY
	/^(\d{1,2})\/(\d{1,2})\/(\d{2})$/, // M/D/YY, MM/DD/YY, MM/D/YY, M/DD/YY
];
const TIME_PART = /^\d{1,2}:\d{2} (AM|PM|am|pm)$/;
const pad2 = (n: number) => String(n).padStart(2, "0");

/**
 * Two-digit years resolve through a sliding window, [now.year - 50,
 * now.year + 49]. With now in 2026: `26` -> 2026, `75` -> 2075, `76` -> 1976.
 * This replaces dayjs's fixed 68/69 cutoff, which is why dayjs `YY` is never
 * used for parsing here.
 */
function resolveShortYear(yy: number, now: Dayjs) {
	const windowStart = now.year() - 50;
	const year = windowStart - (windowStart % 100) + yy;
	return year < windowStart ? year + 100 : year;
}

/**
 * Rewrites a two-digit-year date (`010126`, `1/1/26`, `01/01/26`, plus a time
 * part with `withTime`) as `MM/DD/YYYY` text, keeping the time part as typed.
 * Returns `null` for anything else, including text that is already a
 * four-digit-year date, dates that do not exist (`022929`) and a time the
 * strict parse rejects (`13:99 AM`). A non-null result always parses.
 */
export function normalizeShortYearText(
	text: string,
	opts: TypedDateOptions = {},
): string | null {
	const now = opts.now ?? dayjs();
	let datePart = text.trim();
	let timePart = "";
	if (opts.withTime) {
		const space = datePart.indexOf(" ");
		if (space < 0) return null;
		timePart = datePart.slice(space + 1).trim();
		datePart = datePart.slice(0, space);
		if (!TIME_PART.test(timePart)) return null;
	}
	for (const pattern of SHORT_DATE_PATTERNS) {
		const match = pattern.exec(datePart);
		if (!match) continue;
		const month = Number(match[1]);
		const day = Number(match[2]);
		const year = resolveShortYear(Number(match[3]), now);
		const date = `${pad2(month)}/${pad2(day)}/${year}`;
		const normalized = timePart ? `${date} ${timePart}` : date;
		const formats = opts.withTime
			? DATE_TIME_INPUT_FORMATS
			: DATE_INPUT_FORMATS;
		return parseStrict(normalized, formats) ? normalized : null;
	}
	return null;
}

function parseStrict(text: string, formats: string[]) {
	for (const format of formats) {
		const parsed = dayjs(text, format, true);
		if (parsed.isValid()) return parsed;
	}
	return null;
}

/**
 * Reads what a person typed into a date field: the four-digit-year input
 * list first, then the two-digit-year forms through `normalizeShortYearText`.
 * Strict: `13/01/2026`, `0101202`, `01-01-2026` and `1.1.26` are `null`.
 */
export function parseTypedDate(
	text: string,
	opts: TypedDateOptions = {},
): Dayjs | null {
	const trimmed = text.trim();
	if (!trimmed) return null;
	const formats = opts.withTime ? DATE_TIME_INPUT_FORMATS : DATE_INPUT_FORMATS;
	const long = parseStrict(trimmed, formats);
	if (long) return long;
	const normalized = normalizeShortYearText(trimmed, opts);
	return normalized ? parseStrict(normalized, formats) : null;
}

type DateType = string | number | Date | Dayjs;
type PriodType = "month" | "quarter" | "year";

export function sortByDate(a: string, b: string) {
	return dayjs(a || "0").unix() - dayjs(b || "0").unix();
}

const warnedValues = new Set<string>();

/** Dev-only warning, once per distinct value, for a date that cannot be read. */
function warnUnparseable(helper: string, value: unknown) {
	if (process.env.NODE_ENV === "production") return;
	const key = `${helper}:${String(value)}`;
	if (warnedValues.has(key)) return;
	warnedValues.add(key);
	console.warn(`${helper}: cannot read ${JSON.stringify(value)} as a date`);
}

const ISO_DAY = /^(\d{4}-\d{2}-\d{2})(?:$|[T\s])/;

/** A string as a calendar day: an ISO day is read strictly off its text. */
function parseDayString(value: string): Dayjs {
	const iso = ISO_DAY.exec(value);
	return iso
		? dayjs(iso[1], WIRE_DATE_FORMAT, true)
		: dayjs(value.split("T")[0]);
}

/**
 * A calendar date as display text, `MM/DD/YYYY` unless `format` says
 * otherwise. An ISO string is read as the day in its text (the `T...` part is
 * dropped), so a date never shifts by timezone, and a day that does not exist
 * (`2026-02-31`) is unreadable rather than rolled over. Empty or unreadable
 * input returns "".
 */
export function formatDate(value?: DateType | null, format?: string) {
	if (!value) return "";

	const parsed =
		typeof value === "string"
			? parseDayString(value)
			: typeof (value as Dayjs).format === "function"
				? (value as Dayjs)
				: dayjs(value);
	if (!parsed.isValid()) {
		warnUnparseable("formatDate", value);
		return "";
	}
	return parsed.format(format || DATE_FORMAT);
}

const UTC = "UTC";

const dayFormatters = new Map<string, Intl.DateTimeFormat>();

/** One formatter per zone: building an Intl formatter is slow in big tables. */
function dayFormatter(timeZone: string) {
	const cached = dayFormatters.get(timeZone);
	if (cached) return cached;
	const formatter = new Intl.DateTimeFormat("en-US", {
		timeZone,
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
	});
	dayFormatters.set(timeZone, formatter);
	return formatter;
}

/**
 * An INSTANT (a timestamp, e.g. finishedAt, createdAt) shown as a calendar
 * day, "09/25/2026" (MM/DD/YYYY), in `timeZone` (a company's IANA zone). Empty or invalid
 * zones fall back to UTC; Intl throws a RangeError on an unknown name, which
 * must not break the page. Never use this for a calendar `date` value (a
 * review's from/to): those are already days and must not be shifted.
 */
export function formatInstantDate(
	value: string | Date | undefined | null,
	timeZone: string | undefined | null,
): string {
	if (!value) return "";
	const instant = value instanceof Date ? value : new Date(value);
	if (Number.isNaN(instant.getTime())) return "";
	let formatter: Intl.DateTimeFormat;
	try {
		formatter = dayFormatter(timeZone || UTC);
	} catch {
		formatter = dayFormatter(UTC);
	}
	return formatter.format(instant);
}

/**
 * A date with a time as display text, `MM/DD/YYYY h:mm A` unless `format`
 * says otherwise. Empty or unreadable input returns "".
 */
export function formatDateTime(value?: DateType | null, format?: string) {
	if (!value) return "";
	const parsed = dayjs(value);
	if (!parsed.isValid()) {
		warnUnparseable("formatDateTime", value);
		return "";
	}
	return parsed.format(format || DATE_TIME_FORMAT);
}

export function parseDate(value: DateType) {
	if (value === "Invalid Date") return null;
	return dayjs(value);
}

export function fromNow(value: DateType) {
	return dayjs(value).fromNow();
}

export function yearStart(format?: string) {
	return dayjs()
		.startOf("year")
		.format(format || "YYYY-MM-DD");
}

export function monthStart(format?: string) {
	return dayjs()
		.startOf("month")
		.format(format || "YYYY-MM-DD");
}

export function prevMonthStart(date?: DateType, format?: string) {
	return dayjs(date)
		.subtract(1, "month")
		.startOf("month")
		.format(format || "YYYY-MM-DD");
}

export function prevMonthEnd(format?: string) {
	return dayjs()
		.subtract(1, "month")
		.endOf("month")
		.format(format || "YYYY-MM-DD");
}

export function periodEnd(
	date: DateType,
	period: PriodType = "month",
	format?: string,
) {
	return dayjs(date)
		.endOf(period)
		.format(format || "YYYY-MM-DD");
}

export function parseDateRage(value?: [string, string]) {
	if (!value) {
		return [
			dayjs().subtract(1, "month").startOf("month"),
			dayjs().subtract(1, "month").endOf("month"),
		];
	}
	return value.map((date) => dayjs(date)) as [Dayjs, Dayjs];
}

// TODO: fix types
export const formatDateByPeriod = (date: any, period: PriodType = "month") => {
	if (!date) return "";
	if (date.length === 10) return date;

	switch (period) {
		case "month":
			return dayjs(date + "-01").format("YYYY-MM-DD");
		case "quarter": {
			const quarters = ["01", "04", "07", "10"];
			const [year, q] = date.split("-");
			console.log(date, "q", q, "year", year);
			const month = quarters[q.replace("Q", "") - 1];
			return year + "-" + month + "-01";
		}
		case "year":
			console.log("1111", date);
			return date.split("-")[0] + "-01-01";
		default:
			return "";
	}
};

/**
 * Normalizes various date formats to M/D/YYYY format for CSVBox compatibility
 * @param dateStr - The date string to normalize
 * @returns The normalized date string in M/D/YYYY format
 */
export const normalizeDateFormat = (dateStr: string): string => {
	if (!dateStr) return dateStr;

	// Common date formats from bank CSV files
	const formats = [
		"M/D/YYYY",
		"MM/DD/YYYY",
		"M-D-YYYY",
		"MM-DD-YYYY",
		"YYYY-MM-DD",
		"YYYY/MM/DD",
		"DD/MM/YYYY", // European format
		"DD-MM-YYYY", // European format
		"DD.MM.YYYY", // European format with dots
		"M/D/YY",
		"MM/DD/YY",
		"MMM D, YYYY", // Jan 1, 2024
		"MMMM D, YYYY", // January 1, 2024
		"D MMM YYYY", // 1 Jan 2024
		"D MMMM YYYY", // 1 January 2024
	];

	// Try each format with strict parsing
	for (const format of formats) {
		const parsed = dayjs(dateStr, format, true);
		if (parsed.isValid()) {
			// Return normalized to M/D/YYYY format
			return parsed.format("M/D/YYYY");
		}
	}

	// If no format matches, try dayjs default parsing as fallback
	const fallbackParsed = dayjs(dateStr);
	if (fallbackParsed.isValid()) {
		return fallbackParsed.format("M/D/YYYY");
	}

	// Return original string if no parsing succeeds
	return dateStr;
};
